import { baseApi } from './base-api';
import { API_ENDPOINTS } from '../utils/constants';
import { io } from 'socket.io-client';
import type { InboxFilterResult } from '~/utils/inbox-filters';
import { getIdentityGeneration } from '~/utils/identity-scope';
import { parseApiResponse } from '~/utils/api-errors';

export interface Conversation {
  id: string;
  title?: string;
  bookmarked?: boolean;
  state?: string | null;
  last_message_role?: string | null;
  inbox?: {
    provider: 'outlook' | 'gmail' | 'zendesk' | 'gorgias' | 'dixa';
    account_id?: string | null;
    source_conversation_id?: string;
    owner_user_id?: string | null;
    url?: string | null;
    processed?: boolean;
    current_filter?: InboxFilterResult | null;
    latest_source_timestamp?: string | null;
  } | null;
}

export interface ConversationResponse {
  type: 'success';
  response: Conversation;
}

export interface ConversationUpdate {
  bookmarked?: boolean;
  state?: string | null;
  testing_purpose?: boolean;
  custom_variables?: Record<string, unknown>;
  custom_ids?: Record<string, unknown>;
  identified_by?: Record<string, unknown> | null;
}

export interface ManualActionParam {
  param: string;
  label: string;
  required: boolean;
  type: string;
  description?: string;
}

export interface SalesforceManualAction {
  id: string;
  action_type: 'salesforce_create_case';
  label: string;
  params: ManualActionParam[];
}

export type ManualActionArguments = Record<string, string | string[] | number | boolean>;

export interface MessageAttachment {
  id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  url: string;
  gcs_path?: string;
}

export interface ConversationMessageContextDocument {
  id?: string;
  content?: string;
  prompt_content?: string;
  context?: string;
  source_document_id?: string | null;
  [key: string]: unknown;
}

export interface ConversationMessageContextResponse {
  type: string;
  response: ConversationMessageContextDocument[];
}

export interface ConversationList {
  response: Response;
  type: string;
}

interface Response {
  list: Conversation[];
  total: number;
}

const conversationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversations: builder.query<ConversationList, { filters?: string; page?: number; rows_per_page?: number; select?: string; org_id: string }>({
      query: ({ filters, page, rows_per_page, select }) => {
        const url = new URL('/v1/conversations', API_ENDPOINTS.KARLA_FLASK_API);

        if (filters) url.searchParams.append('filters', filters);
        if (select) url.searchParams.append('select', select);
        if (page !== undefined) url.searchParams.append('page', String(page));
        if (rows_per_page !== undefined) url.searchParams.append('rows_per_page', String(rows_per_page));

        return {
          url: url.toString(),
          method: 'GET',
        };
      },
      providesTags: ['ConversationList'],
    }),

    streamConversations: builder.query<void, string>({
      queryFn: () => ({ data: undefined }),
      async onCacheEntryAdded(org_id, { dispatch, cacheEntryRemoved }) {
        const generation = getIdentityGeneration();
        // exactly one socket per org
        const socket = io(`${API_ENDPOINTS.KARLA_FLASK_API}/conversations`, { transports: ['websocket'], query: { org_id } });

        const seenAgentRequested = new Set<string>();

        const listener = (payload: { type: 'added' | 'modified'; conversation: Conversation } | { type: 'removed'; id: string }) => {
          if (generation !== getIdentityGeneration()) return;
          dispatch(conversationsApi.util.invalidateTags(['Conversation', 'ConversationList']));

          dispatch(
            conversationsApi.util.updateQueryData('getConversations', { org_id, filters: undefined, select: undefined }, (draft) => {
              if (payload.type === 'added') {
                if (!draft.response.list.find((c) => c.id === payload.conversation.id)) {
                  draft.response.list.push(payload.conversation);
                }
              } else if (payload.type === 'modified') {
                const i = draft.response.list.findIndex((c) => c.id === payload.conversation.id);
                if (i !== -1) {
                  if (payload.conversation.state) draft.response.list[i].state = payload.conversation.state;
                  if (payload.conversation.last_message_role !== undefined) draft.response.list[i].last_message_role = payload.conversation.last_message_role;
                }
              } else if (payload.type === 'removed') {
                const removedId = payload.id;
                draft.response.list = draft.response.list.filter((c) => c.id !== removedId);
              }
            }),
          );
          if ((payload.type === 'added' || payload.type === 'modified') && payload.conversation.state === 'agent_requested' && !seenAgentRequested.has(payload.conversation.id)) {
            seenAgentRequested.add(payload.conversation.id);
            window.dispatchEvent(new Event('agentRequested'));
          }
        };

        socket.on('conversation', listener);

        await cacheEntryRemoved;
        socket.off('conversation', listener);
        socket.disconnect();
      },
      keepUnusedDataFor: Infinity,
    }),

    getActiveAgentConversations: builder.query<{ type: string; response: { id: string; state: string; last_message_role: string | null }[] }, string>({
      query: () => ({
        url: `${API_ENDPOINTS.KARLA_FLASK_API}/conversations/agent/active`,
        method: 'GET',
      }),
    }),

    downloadConversations: builder.mutation<Blob, { org_id: string; filters: object; output_type?: 'csv' | 'xlsx' }>({
      query: ({ filters, output_type = 'csv' }) => {
        const url = new URL('/v1/conversations/download', API_ENDPOINTS.KARLA_FLASK_API);

        url.searchParams.append('params', JSON.stringify({ ...filters, output_type }));

        return {
          url: url.toString(),
          method: 'GET',
          responseHandler: (res: globalThis.Response) => (res.ok ? res.blob() : parseApiResponse(res)),
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        const generation = getIdentityGeneration();
        try {
          const { data: blob } = await queryFulfilled;
          if (generation !== getIdentityGeneration()) return;

          const fileURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = fileURL;
          link.setAttribute('download', `conversations.${arg.output_type}`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(fileURL);
        } catch (err) {
          console.error('Download error', err);
        }
      },
    }),
    getConversationById: builder.query<ConversationResponse, string>({
      query: (id) => {
        // Build the URL with query parameters
        const url = new URL(`/v1/conversations/${id}`, API_ENDPOINTS.KARLA_FLASK_API);

        return {
          url: url.toString(),
          method: 'GET', // Using GET to fetch data
        };
      },
      providesTags: (result, error, id) => [{ type: 'Conversation', id }],
    }),
    getConversationMessageContext: builder.query<ConversationMessageContextResponse, { conversation_id: string; message_id: string }>({
      query: ({ conversation_id, message_id }) => {
        // Build the URL with query parameters
        const url = new URL(`/v1/conversations/${conversation_id}/messages/${message_id}/context`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'GET', // Using GET to fetch data
        };
      },
    }),
    getConversationLogs: builder.query<{ type: 'success'; response: { logs?: Array<{ event_type?: string; timestamp?: string; input?: unknown; output?: unknown }>; [key: string]: unknown } }, { conversation_id: string }>({
      query: ({ conversation_id }) => {
        // Build the URL with query parameters
        const url = new URL(`/v1/conversations/${conversation_id}/logs`, API_ENDPOINTS.KARLA_FLASK_API);
        return {
          url: url.toString(),
          method: 'GET', // Using GET to fetch data
        };
      },
      providesTags: (result, error, { conversation_id }) => [{ type: 'ConversationLogs', id: conversation_id }],
    }),
    translateConversation: builder.mutation<{ id: string; content: string }[], { conversation_id: string; model_id: string; org_id: string; user_id: string }>({
      query: ({ conversation_id, model_id, org_id, user_id }) => ({
        url: `/conversations/${conversation_id}/translate`,
        method: 'POST',
        body: { conversation_id, model_id, org_id, user_id },
        responseHandler: 'text',
      }),
      //transform raw text to json parsed
      transformResponse: (raw: string): { id: string; content: string }[] => {
        try {
          const json = JSON.parse(raw);
          if (Array.isArray(json)) return json;
          if (json?.response && Array.isArray(json.response)) {
            return json.response;
          }
        } catch (e) {
          console.error('translate: failed to parse JSON', e, raw);
        }
        return [];
      },
    }),
    updateConversation: builder.mutation<{ type: 'success'; response: boolean }, { id: string; updatedData: ConversationUpdate }>({
      query: ({ id, updatedData }) => ({
        url: `conversations/${id}`,
        method: 'PATCH',
        body: updatedData,
      }),
      invalidatesTags: (result, error, { id }) => (error ? [] : [{ type: 'Conversation', id }, 'ConversationList']),
    }),
    deleteConversation: builder.mutation<void, string | undefined>({
      query: (id) => ({
        url: `conversations/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error) => (error ? [] : ['ConversationList']),
    }),
    deleteConversations: builder.mutation<void, string[]>({
      query: (ids) => ({
        url: 'conversations',
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: (result, error) => (error ? [] : ['ConversationList']),
    }),
    // New POST request endpoint
    sendAgentWebhook: builder.mutation<void, { conversation_id: string; body: any }>({
      query: ({ conversation_id, body }) => ({
        url: `/conversations/${conversation_id}/agent/webhooks`,
        method: 'POST',
        body, // The body of the POST request
      }),
      // You might want to invalidate tags or perform optimistic updates here
      // depending on the expected behavior after sending a webhook.
      // For now, no specific invalidation is added as the effect on cache is unknown.
    }),
    getConversationManualActions: builder.query<{ type: 'success'; response: { actions: SalesforceManualAction[] } }, string>({
      query: (conversation_id) => ({
        url: `/conversations/${conversation_id}/actions/manual`,
        method: 'GET',
      }),
    }),
    triggerConversationAction: builder.mutation<{ type: 'success'; response: { executed: boolean } }, { conversation_id: string; action_type: string; action_node_id?: string; arguments: ManualActionArguments }>({
      query: ({ conversation_id, action_type, action_node_id, arguments: args }) => ({
        url: `/conversations/${conversation_id}/actions/trigger`,
        method: 'POST',
        body: { arguments: args, action_type, ...(action_node_id ? { action_node_id } : {}) },
      }),
    }),
    uploadAgentAttachment: builder.mutation<{ type: string; response: MessageAttachment }, { conversation_id: string; file: File }>({
      query: ({ conversation_id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/v1/conversations/${conversation_id}/attachments`,
          method: 'POST',
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useGetConversationManualActionsQuery,
  useTriggerConversationActionMutation,
  useDeleteConversationsMutation,
  useDownloadConversationsMutation,
  useGetConversationsQuery,
  useLazyGetConversationLogsQuery,
  useLazyGetConversationMessageContextQuery,
  useGetConversationByIdQuery,
  useUpdateConversationMutation,
  useDeleteConversationMutation,
  useTranslateConversationMutation,
  useSendAgentWebhookMutation,
  useUploadAgentAttachmentMutation,
  useStreamConversationsQuery,
  useGetActiveAgentConversationsQuery,
} = conversationsApi;

export { conversationsApi };
